import { useState, ChangeEvent, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { initializeQuestion } from "~/redux/modules/admin/data";
import {
  setLoading,
  setSuccess,
  setError,
  resetAlert,
} from "~/redux/modules/admin/screen";
import { RootState } from "~/redux/rootReducer";
import { getColumns } from "../const/column";
import { Option } from "~/types/component";
import { Question, QuestionText, Answer } from "~/types/question";
import { questionsCollection } from "~/const/firestore";
import { Event } from "~/types/component";

export type Module = ReturnType<typeof useModule>;

export const useModule = () => {
  const dispatch = useDispatch();

  const master = useSelector((state: RootState) => state.master);
  const _questions = useSelector(
    (state: RootState) => state.adminData.questions
  );
  const isQuestionInit = useSelector(
    (state: RootState) => state.adminData.isQuestionInit
  );
  const _categories = master.categories;

  // カラムデータ
  const columns = getColumns(master);

  // 画面の状態を制御
  const startLoading = () => dispatch(setLoading(true));
  const stopLoading = () => dispatch(setLoading(false));
  const resetAlertMessage = () => dispatch(resetAlert());
  const setSuccessMessage = (val: string) => dispatch(setSuccess(val));
  const setErrorMessage = (val: string) => dispatch(setError(val));

  // 会話パターンリスト 検索条件
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const changeIsDeletedValue = (e: ChangeEvent<HTMLInputElement>) =>
    setIsDeleted(e.target.checked);
  const [categories, setCategories] = useState<Option[]>([]);
  useEffect(() => {
    let _questionsCp = _questions;
    if (isDeleted === false) {
      _questionsCp = _questions.filter((item) => item.isDeleted === false);
    }

    const _options: Option[] = [{ label: "全て", value: -1 }, ..._categories];
    const options = _options.map((option) => {
      let count = 0;
      if (option.value === -1) {
        count = _questionsCp.length;
      } else {
        const _filterQuestion = _questionsCp.filter(
          (item) => item.categoryId === option.value
        );
        count = _filterQuestion.length;
      }
      return {
        label: `${option.label}(${count})`,
        value: option.value,
      };
    });
    setCategories(options);
  }, [_categories, _questions, isDeleted]);
  const [categoryId, setCategoryId] = useState<number>(-1);
  const changeCategoryIdValue = (e: Event) =>
    setCategoryId(parseInt(e.target.value));

  // 会話パターンデータ
  const [questions, setQuestions] = useState<Question[]>([]);
  useEffect(() => {
    let _questionsCp = _questions;
    if (isDeleted === false) {
      _questionsCp = _questions.filter((item) => item.isDeleted === false);
    }

    if (categoryId === -1) {
      setQuestions(_questionsCp);
    } else {
      setQuestions(
        _questionsCp.filter((item) => item.categoryId === categoryId)
      );
    }
  }, [_questions, categoryId, isDeleted]);

  // ダイアログ データ
  const initialAnswerOption: Answer = {
    text: "",
    reaction: "",
    value: "",
    face: "fun",
    textAnswer: false,
  };
  const initialQuestionText: QuestionText = {
    face: "fun",
    text: "",
  };
  const initialQuestion: Question = {
    questionText: [initialQuestionText],
    textAnswer: true,
    categoryId: 0,
    largeLabel: "",
    mediumLabel: "",
    smallLabel: "",
    multiple: false,
    answerOptions: [initialAnswerOption],
    isNextQuestion: false,
    isDeleted: false,
  };
  const [dialogData, setDialogData] = useState<Question>(initialQuestion);

  // 回答アコーディオン 開閉
  const [acordionOpen, setAcordionOpen] = useState<boolean[]>([]);
  const handleChangeAcordionOpen = (index: number, bool: boolean) => {
    const result: boolean[] = [];
    acordionOpen.forEach((item, i) => {
      if (i === index) {
        result.push(bool);
      } else {
        result.push(item);
      }
    });
    setAcordionOpen(result);
  };

  // ダイアログのデータを変更
  const changeValue = (value: unknown, key: keyof Question) => {
    setDialogData({ ...dialogData, [key]: value });
  };
  const changeQuestionText = (
    value: any,
    index: number,
    key: keyof QuestionText
  ) => {
    let cpDatas = dialogData.questionText;
    let cpData = cpDatas[index];
    const data: QuestionText = { ...cpData, [key]: value };
    const result: QuestionText[] = [];
    cpDatas.forEach((item, i) => {
      if (i === index) {
        result.push(data);
      } else {
        result.push(item);
      }
    });
    changeValue(result, "questionText");
  };
  const changeAnswerOption = (value: any, index: number, key: keyof Answer) => {
    let cpDatas = dialogData.answerOptions;
    let cpData = cpDatas[index];
    const data: Answer = { ...cpData, [key]: value };
    const result: Answer[] = [];
    cpDatas.forEach((item, i) => {
      if (i === index) {
        result.push(data);
      } else {
        result.push(item);
      }
    });
    changeValue(result, "answerOptions");
  };

  // 質問文を追加
  const addQuestionTextOption = () => {
    const data = dialogData.questionText.concat([initialQuestionText]);
    changeValue(data, "questionText");
  };

  // 回答を削除
  const deleteQuestionTextOption = (index: number) => {
    const result: QuestionText[] = [];
    dialogData.questionText.forEach((item, i) => {
      if (index !== i) {
        result.push(item);
      }
    });
    changeValue(result, "questionText");
  };

  // 回答を追加
  const addAnswerOption = () => {
    const data = dialogData.answerOptions.concat([initialAnswerOption]);
    changeValue(data, "answerOptions");
  };

  // 回答を削除
  const deleteAnswerOption = (index: number) => {
    const result: Answer[] = [];
    dialogData.answerOptions.forEach((item, i) => {
      if (index !== i) {
        result.push(item);
      }
    });
    changeValue(result, "answerOptions");
  };

  // バリデーション
  const _validate = (): boolean => {
    // 大項目が未入力の場合
    if (dialogData.largeLabel === "") {
      return false;
    }

    // 質問文の入力に問題がある場合
    if (dialogData.questionText.length === 0) {
      return false;
    } else {
      let isSuccess = true;
      for (let i = 0; i < dialogData.questionText.length; i++) {
        const item = dialogData.questionText[i];
        if (_questionTextValidate(item) === false) {
          isSuccess = false;
        }
      }
      if (!isSuccess) return false;
    }

    // 回答が一つもない時
    if (dialogData.answerOptions.length === 0) {
      return false;
    }

    // 回答タイプが自由回答の場合
    if (dialogData.textAnswer === true) {
      // SHUZOの表情とSHUZOの反応は、必須項目
      const firstAnswerOption = dialogData.answerOptions[0];
      if (!firstAnswerOption.face) {
        return false;
      }
      if (!firstAnswerOption.reaction) {
        return false;
      }
    }

    // 回答タイプが選択式の場合
    if (dialogData.textAnswer === false) {
      let isSuccess = true;
      for (let i = 0; i < dialogData.answerOptions.length; i++) {
        const item = dialogData.answerOptions[i];
        if (_answerOptionValidate(item) === false) {
          isSuccess = false;
        }
      }
      if (!isSuccess) return false;
    }

    return true;
  };

  const _questionTextValidate = (questionText: QuestionText): boolean => {
    // SHUZOロボの表情が未入力の時
    if (!questionText.face) return false;

    // SHUZOロボの質問文が未入力の時
    if (!questionText.text) return false;

    return true;
  };

  // answerOption用のバリデーション
  const _answerOptionValidate = (answerOption: Answer): boolean => {
    // 選択肢の値が未入力の時
    if (!answerOption.text) return false;

    // ノートに登録される値が未入力の時(textAnswerがtrueの時は必要なし)
    if (!answerOption.textAnswer) {
      if (!answerOption.value) return false;
    }

    // SHUZOロボの返答が未入力の時
    if (!answerOption.reaction) return false;

    // SHUZOロボの表情が未入力の時
    if (!answerOption.face) return false;

    return true;
  };

  // 登録ボタン disabled
  const saveDisabled = !_validate();

  // 回答 answerOption エラー表示
  const [answerOptionError, setAnswerOptionError] = useState<boolean[]>([]);
  useEffect(() => {
    const errors: boolean[] = [];
    if (dialogData.textAnswer) {
      // 自由回答
      let _error = false;
      errors.push(_error);
    } else {
      // 選択式
      dialogData.answerOptions.forEach((item) => {
        errors.push(!_answerOptionValidate(item));
      });
    }
    setAnswerOptionError(errors);
  }, [dialogData.answerOptions, dialogData.textAnswer]);

  // 回答を追加ボタン・削除ボタン disabled
  const addDisabled = dialogData.answerOptions.length >= 10;
  const deleteDisabled = dialogData.answerOptions.length <= 1;

  // 質問データ 登録or更新
  const onClickSave = async () => {
    startLoading();
    setOpenDialog(false);
    resetAlertMessage();

    // 回答タイプが自由回答の場合
    if (dialogData.textAnswer === true) {
      const { face, reaction } = dialogData.answerOptions[0];
      const answerOption: Answer = {
        face: face,
        reaction: reaction,
        text: "",
        value: "",
        textAnswer: true,
      };
      changeValue([answerOption], "answerOptions");
    }

    // 回答タイプが選択式の場合
    if (dialogData.textAnswer === false) {
      const answerOptions: Answer[] = [];
      for (let i = 0; i < dialogData.answerOptions.length; i++) {
        const item = dialogData.answerOptions[i];

        // テキスト入力の場合は、valueに空文字をセット
        if (item.textAnswer) {
          answerOptions.push({ ...item, value: "" });
          console.log(answerOptions);
        } else {
          answerOptions.push(item);
        }
      }
      changeValue(answerOptions, "answerOptions");
    }

    if (dialogData.docID === undefined) {
      // 新規登録
      try {
        await questionsCollection.add(dialogData);
        dispatch(initializeQuestion());
        setTimeout(
          () => setSuccessMessage("会話パターンの登録をしました"),
          100
        );
      } catch {
        setErrorMessage("会話パターンの登録に失敗しました");
      }
    } else {
      // 更新
      try {
        await questionsCollection
          .doc(dialogData.docID)
          .set(dialogData, { merge: true });
        dispatch(initializeQuestion());
        setTimeout(
          () => setSuccessMessage("会話パターンの更新をしました"),
          100
        );
      } catch {
        setErrorMessage("会話パターンの更新に失敗しました");
      }
    }

    stopLoading();
  };

  // 質問データ 削除
  const onClickDelete = async () => {
    const result = confirm("本当に削除しますか？");
    if (result) {
      startLoading();
      setOpenDialog(false);
      resetAlertMessage();

      if (dialogData.docID !== undefined) {
        try {
          await questionsCollection
            .doc(dialogData.docID)
            .set({ isDeleted: true }, { merge: true });
          dispatch(initializeQuestion());
          setTimeout(
            () => setSuccessMessage("会話パターンの削除をしました"),
            100
          );
        } catch {
          setErrorMessage("会話パターンの削除に失敗しました");
        }
      } else {
        setErrorMessage("会話パターンの削除に失敗しました");
      }

      stopLoading();
    }
  };
  const onClickRestore = async () => {
    const result = confirm("本当に復元しますか？");
    if (result) {
      startLoading();
      setOpenDialog(false);
      resetAlertMessage();

      if (dialogData.docID !== undefined) {
        try {
          await questionsCollection
            .doc(dialogData.docID)
            .set({ isDeleted: false }, { merge: true });
          dispatch(initializeQuestion());
          setTimeout(
            () => setSuccessMessage("会話パターンの復元をしました"),
            100
          );
        } catch {
          setErrorMessage("会話パターンの復元に失敗しました");
        }
      } else {
        setErrorMessage("会話パターンの復元に失敗しました");
      }

      stopLoading();
    }
  };

  // ダイアログ 開閉
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const handleOpenDialog = (data?: Question) => {
    if (data) {
      setDialogData(data);
    } else {
      setDialogData(initialQuestion);
    }
    setOpenDialog(true);
  };
  const handleCloseDialog = () => setOpenDialog(false);

  // ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return {
    states: {
      columns,
      questions,
      isQuestionInit,
      categoryId,
      categories,
      isDeleted,

      // ダイアログ
      openDialog,
      acordionOpen,
      answerOptionError,
      dialogData,
      addDisabled,
      deleteDisabled,
      saveDisabled,

      // ページネーション
      page,
      rowsPerPage,
    },
    setters: {
      changeCategoryIdValue,
      changeIsDeletedValue,

      // ダイアログ
      handleOpenDialog,
      handleCloseDialog,
      handleChangeAcordionOpen,
      changeValue,
      changeQuestionText,
      changeAnswerOption,

      // ページネーション
      handleChangePage,
      handleChangeRowsPerPage,
    },
    actions: {
      addQuestionTextOption,
      deleteQuestionTextOption,
      addAnswerOption,
      deleteAnswerOption,
      onClickSave,
      onClickDelete,
      onClickRestore,
    },
  };
};
